import { K } from '../kabinet/atoms';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * «КАБИНЕТЫ 3 ИЗ 5», «КАМПАНИИ 40 ИЗ 50» — В ПОЛОСЕ ИТОГОВ.
 *
 * Этого не было вовсе, и предел тарифа человек узнавал единственным
 * способом: упершись в отказ подключить следующий кабинет. Отказ
 * приходит в тот момент, когда решение уже принято, — и выглядит
 * как поломка, а не как условие тарифа.
 *
 * ПОЛОСКА, А НЕ ТОЛЬКО ЧИСЛО. «40 из 50» надо прочитать и поделить;
 * полоска отвечает на «много ли осталось» раньше, чем глаз дочитает
 * второе число. Оба вида нужны: полоска без числа не говорит,
 * сколько именно.
 *
 * ПОДПИСЬ ПРИ НАВЕДЕНИИ НАЗЫВАЕТ ОСТАТОК СЛОВАМИ («можно подключить
 * ещё 2 кабинета») — так в эталоне. Это ответ на вопрос, который
 * человек задаёт после того, как прочитал числа.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function Предел({
  имя,
  узел,
  занято,
  предел,
  подпись,
  полоской = true,
  краскаПолоски = 'var(--brand)',
}: {
  имя: string;
  /**
   * Имя узла эталона. Прежде выводилось из подписи, и это работало,
   * пока пределов было два. С 07.09.2026 их четыре, и «Ставки» на 390
   * против «Тесты ставок» на широком дали бы два разных узла у одной
   * и той же пары. Имя задаётся снаружи и от подписи не зависит.
   */
  узел: 'предел-кабинетов' | 'предел-кампаний' | 'предел-тестов-ставок' | 'предел-тестов-фото';
  занято: number;
  предел: number;
  подпись: string;
  /**
   * Рисовать ли полоску заполнения.
   *
   * НА ТЕЛЕФОНЕ ЕЁ НЕТ, И ЭТО НЕ УПРОЩЕНИЕ. В эталоне на 390 строка
   * пределов стоит отдельной плашкой над таблицей и состоит только
   * из чисел: «Кабинеты 3 из 5 · Кампании 40 из 50». Полоска
   * в тридцать точек шириной на телефоне не отвечает на «много ли
   * осталось» — она просто занимает место, которого там нет.
   */
  полоской?: boolean;
  /**
   * ═══════════════════════════════════════════════════════════════════
   * ЦВЕТ ЗАПОЛНЕНИЯ — СНАРУЖИ, А НЕ ЧАСТИЦЕЙ, И ЭТОМУ ЕСТЬ ПРИЧИНА.
   *
   * У пары ABC-тестов полоска цвета `--serious` (передача 07.09.2026,
   * раздел 3.2). Вырезать это правило из эталона НЕЛЬЗЯ: у стенда мест
   * под ABC ровно столько, сколько занято, — предел достигнут всегда,
   * и полоска в снимке всегда оранжевая (`--warn`). Цвета `--serious`
   * в отрисованном эталоне нет ни разу.
   *
   * Поэтому он приходит переменной оформления отсюда. Это тот же
   * источник правды (`bidra-tokens.css`), только другой путь.
   * ═══════════════════════════════════════════════════════════════ */
  краскаПолоски?: string;
}) {
  /* Полоска не переливается за край: заняли больше, чем даёт тариф
     (так бывает после понижения) — рисуем полной, а не в 120 %. */
  const доля = предел <= 0 ? 0 : Math.min(100, Math.round((занято / предел) * 100));

  /**
   * ═══════════════════════════════════════════════════════════════════
   * МЕСТ БОЛЬШЕ НЕТ — ЧИСЛО И ПОЛОСКА КРАСНЕЮТ. ДО 27.08 НЕ КРАСНЕЛИ.
   *
   * Стояло «полоска заполнения всегда цвета марки — так в эталоне при
   * любом заполнении», и это было неверно: у эталона при «50 из 50»
   * и число `--warn-ink`, и заполнение `--warn`. То есть исчерпанный
   * тариф выглядел у нас ровно так же, как наполовину занятый —
   * «всё в порядке», — и человек узнавал правду отказом при попытке
   * взять новую кампанию.
   *
   * Прочитать это можно было только с экрана `кампаний под завязку`,
   * а он до сегодня в сверку не входил.
   * ═══════════════════════════════════════════════════════════════════
   */
  const впритык = предел > 0 && занято >= предел;

  return (
    <span data-d={узел} title={подпись} className={K.пределРяд}>
      <span className={K.подписьПредела}>{имя}</span>
      <b className={впритык ? K.числоПределаТревога : K.числоПредела}>
        {занято} из {предел}
      </b>
      {полоской && (
        <span aria-hidden="true" className={K.полоскаПредела}>
          <span
            className={впритык ? K.заполнениеПределаТревога : K.заполнениеПредела}
            style={впритык ? { width: `${доля}%` } : { width: `${доля}%`, background: краскаПолоски }}
          />
        </span>
      )}
    </span>
  );
}

/** «можно подключить ещё 2 кабинета» / «свободно ещё 10 мест». */
export function остатокСловами(занято: number, предел: number, что: 'кабинет' | 'место'): string {
  const свободно = Math.max(0, предел - занято);
  if (свободно === 0) {
    /* Слова эталона: подсказка не только сообщает беду, но и называет
       выход, до которого человек сам не додумается («снимите
       с управления другую» — не «купите тариф»). */
    return что === 'кабинет'
      ? 'предел тарифа: чтобы подключить ещё один, отключите другой кабинет'
      : 'предел тарифа: чтобы взять новую, снимите с управления другую';
  }
  const слово = что === 'кабинет' ? склонение(свободно, 'кабинет', 'кабинета', 'кабинетов')
                                  : склонение(свободно, 'место', 'места', 'мест');
  return что === 'кабинет'
    ? `можно подключить ещё ${свободно} ${слово}`
    : `свободно ещё ${свободно} ${слово}`;
}

function склонение(n: number, один: string, два: string, много: string): string {
  const последняя = n % 10;
  const подросток = n % 100 >= 11 && n % 100 <= 14;
  if (подросток) return много;
  if (последняя === 1) return один;
  if (последняя >= 2 && последняя <= 4) return два;
  return много;
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * СТРОКА ПРЕДЕЛОВ — ДВУМЯ РАЗНЫМИ ВИДАМИ, А НЕ ОДНИМ ПОУЖЕ.
 *
 * На широком она стоит В ПОЛОСЕ ИТОГОВ, рядом с «Итого» и ДРР:
 * оба числа про то, что сейчас показано. На 390 полосы итогов нет —
 * там строка пределов вынесена ОТДЕЛЬНОЙ ПЛАШКОЙ над таблицей,
 * с рамкой, своей подложкой и точкой между числами.
 *
 * Так в эталоне, и по делу: на телефоне полоса итогов уходит
 * в три строки, и пределы в ней теряются между «Итого» и «ДРР».
 * Отдельная плашка отвечает на «сколько у меня осталось» с одного
 * взгляда — а спрашивают это как раз с телефона, стоя в чужом
 * кабинете Wildberries.
 * ═══════════════════════════════════════════════════════════════════════
 */
export interface ПределТестов {
  занято: number;
  предел: number | null;
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ПОРЯДОК ПАР НА 390 ДРУГОЙ, ЧЕМ НА ШИРОКОМ, И ЭТО ЗАМЕР, А НЕ ВКУС.
 *
 * Широкая полоса: Кабинеты · Кампании · Тесты ставок · ABC-тесты.
 * Плашка на 390: Кабинеты · Ставки · ABC · Кампании.
 *
 * Так в эталоне. Довод виден на самой плашке: на телефоне пары
 * переносятся, и «Кампании» — самая длинная — уходит в конец, чтобы
 * не рвать строку посередине.
 *
 * ПОДПИСИ НА 390 КОРОЧЕ: «Ставки» и «ABC» вместо «Тесты ставок»
 * и «ABC-тесты». Полосок там нет вовсе — тридцать точек ширины
 * на телефоне не отвечают на «много ли осталось».
 * ═══════════════════════════════════════════════════════════════════════
 */
export function ПределыПлашкой({
  кабинеты,
  кампании,
  тестыСтавок = null,
  тестыФото = null,
}: {
  кабинеты: { used: number; limit: number } | null;
  кампании: { used: number; limit: number } | null;
  тестыСтавок?: ПределТестов | null;
  тестыФото?: ПределТестов | null;
}) {
  const ставки = тестыСтавок !== null && тестыСтавок.предел !== null ? тестыСтавок : null;
  const фото = тестыФото !== null && тестыФото.предел !== null ? тестыФото : null;
  const пары = [кабинеты, ставки, фото, кампании].filter((п) => п !== null).length;
  if (пары === 0) return null;

  let нарисовано = 0;
  const точка = () => {
    нарисовано += 1;
    return нарисовано < пары ? (
      <span aria-hidden="true" className={K.точкаМеждуПределами}>
        ·
      </span>
    ) : null;
  };

  return (
    <div data-d="строка-состояния" className={K.строкаСостояния}>
      {кабинеты !== null && (
        <>
          <Предел
            имя="Кабинеты"
            узел="предел-кабинетов"
            занято={кабинеты.used}
            предел={кабинеты.limit}
            подпись={остатокСловами(кабинеты.used, кабинеты.limit, 'кабинет')}
            полоской={false}
          />
          {точка()}
        </>
      )}
      {ставки !== null && (
        <>
          <Предел
            имя="Ставки"
            узел="предел-тестов-ставок"
            занято={ставки.занято}
            предел={ставки.предел!}
            подпись={подсказкаПределаСтавок(ставки.занято, ставки.предел!)}
            полоской={false}
          />
          {точка()}
        </>
      )}
      {фото !== null && (
        <>
          <Предел
            имя="ABC"
            узел="предел-тестов-фото"
            занято={фото.занято}
            предел={фото.предел!}
            подпись={подсказкаПределаФото(фото.занято, фото.предел!)}
            полоской={false}
          />
          {точка()}
        </>
      )}
      {кампании !== null && (
        <Предел
          имя="Кампании"
          узел="предел-кампаний"
          занято={кампании.used}
          предел={кампании.limit}
          подпись={остатокСловами(кампании.used, кампании.limit, 'место')}
          полоской={false}
        />
      )}
    </div>
  );
}

/**
 * Подсказки пределов тестов.
 *
 * НА ПРЕДЕЛЕ ОНИ НАЗЫВАЮТ ВЫХОД, а не только беду, — тот же приём, что
 * у предела кампаний. У ABC подсказка вдобавок объясняет, ПОЧЕМУ мест
 * меньше: он подменяет главное фото живой карточки.
 */
export function подсказкаПределаСтавок(занято: number, предел: number): string {
  if (занято >= предел) {
    return 'Предел одновременных тестов ставки: чтобы запустить новый, дождитесь конца круга или остановите идущий';
  }
  const свободно = предел - занято;
  return `Тесты ставки идут по кластерам и товарам. Свободно ещё ${свободно} ${склонение(свободно, 'место', 'места', 'мест')}`;
}

export function подсказкаПределаФото(занято: number, предел: number): string {
  if (занято >= предел) {
    return 'Предел одновременных ABC-тестов фото: каждый подменяет главное фото живой карточки, поэтому мест меньше';
  }
  const свободно = предел - занято;
  return `ABC-тесты фото идут по товарам. Свободно ещё ${свободно} ${склонение(свободно, 'место', 'места', 'мест')}`;
}
